import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  CheckSquare,
  FolderKanban,
  LayoutDashboard,
  Menu,
  ScrollText,
  Settings,
  Sliders,
  Users,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import * as Dialog from "@radix-ui/react-dialog";
import { ThemeToggle } from "./ThemeToggle";
import { TopSearch } from "./TopSearch";
import { Wordmark, WordmarkCompact } from "./Wordmark";
import { UserMenu } from "./UserMenu";
import { FeedbackWidget } from "./FeedbackWidget";
import { useApprovalsCount } from "../lib/approvals";
import { useAuthUser } from "../lib/auth";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  badgeKey?: "approvals";
}

const NAV: NavItem[] = [
  { to: "/", label: "Übersicht", icon: LayoutDashboard },
  {
    to: "/approvals",
    label: "Approvals",
    icon: CheckSquare,
    badgeKey: "approvals",
  },
  { to: "/projects", label: "Projekte", icon: FolderKanban },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/rules", label: "Rules", icon: Sliders },
  { to: "/logs", label: "Logs", icon: ScrollText },
  { to: "/settings/whitelist", label: "Einstellungen", icon: Settings },
];

interface SidebarBadgeState {
  count: number;
  pulse: boolean;
}

function NavList({
  collapsed,
  onItemClick,
  badge,
}: {
  collapsed: boolean;
  onItemClick?: () => void;
  badge: SidebarBadgeState;
}) {
  return (
    <Tooltip.Provider delayDuration={150}>
      <nav className="flex-1 space-y-0.5 px-2 py-2">
        {NAV.map(({ to, label, icon: Icon, badgeKey }) => {
          const showBadge = badgeKey === "approvals" && badge.count > 0;
          const link = (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={onItemClick}
              className={({ isActive }) =>
                `flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} rounded-md py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-bg-selected text-text-primary"
                    : "text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                }`
              }
            >
              <Icon size={16} />
              {!collapsed && <span className="flex-1">{label}</span>}
              {showBadge && (
                <span
                  className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent px-1.5 font-mono text-xs font-medium tabular-nums text-text-on-accent ${
                    badge.pulse ? "animate-badge-pulse" : ""
                  } ${collapsed ? "absolute -right-1 -top-1 h-4 min-w-[16px] px-1 text-[10px]" : ""}`}
                  aria-label={`${badge.count} pending`}
                >
                  {badge.count > 99 ? "99+" : badge.count}
                </span>
              )}
            </NavLink>
          );
          if (!collapsed) return link;
          return (
            <Tooltip.Root key={to}>
              <Tooltip.Trigger asChild>
                <div className="relative">{link}</div>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="right"
                  sideOffset={8}
                  className="z-tooltip rounded-md border border-border-primary bg-bg-elevated px-2 py-1 text-xs font-medium text-text-primary shadow-md animate-fade-in"
                >
                  {label}
                  <Tooltip.Arrow className="fill-bg-elevated" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          );
        })}
      </nav>
    </Tooltip.Provider>
  );
}

export function AppShell() {
  const { data: approvalsCountData } = useApprovalsCount({ polling: true });
  const pendingCount = approvalsCountData?.count ?? 0;
  const { data: authUser } = useAuthUser();

  const [pulse, setPulse] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const prevCountRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevCountRef.current;
    if (prev !== null && pendingCount > prev) {
      const delta = pendingCount - prev;
      setPulse(true);
      setAnnouncement(
        `${delta} ${delta === 1 ? "neuer Approval-Eintrag" : "neue Approval-Einträge"}, ${pendingCount} insgesamt offen.`,
      );
      const t = setTimeout(() => setPulse(false), 1100);
      return () => clearTimeout(t);
    }
    prevCountRef.current = pendingCount;
    return undefined;
  }, [pendingCount]);

  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex h-full">
      <a href="#main-content" className="skip-link">
        Zum Hauptinhalt springen
      </a>
      {/* Desktop sidebar (lg+) — full 240px */}
      <aside className="hidden flex-col border-r border-border-primary bg-bg-secondary lg:flex lg:w-sidebar lg:shrink-0">
        <div className="flex h-topbar items-center px-4">
          <Wordmark />
        </div>
        <NavList collapsed={false} badge={{ count: pendingCount, pulse }} />
      </aside>

      {/* Tablet sidebar (md..lg) — 60px icon-rail */}
      <aside className="hidden flex-col border-r border-border-primary bg-bg-secondary md:flex md:w-sidebar-collapsed md:shrink-0 lg:hidden">
        <div className="flex h-topbar items-center justify-center">
          <WordmarkCompact />
        </div>
        <NavList collapsed badge={{ count: pendingCount, pulse }} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-topbar shrink-0 items-center justify-between gap-2 border-b border-border-primary bg-bg-primary px-4 md:px-6">
          {/* Mobile-only: hamburger + wordmark */}
          <button
            type="button"
            aria-label="Menü öffnen"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-bg-hover hover:text-text-primary md:hidden"
          >
            <Menu size={16} />
          </button>
          <Wordmark className="md:hidden" />

          <div className="hidden flex-1 items-center gap-3 md:flex">
            <TopSearch />
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 overflow-auto bg-bg-primary focus:outline-none"
        >
          <Outlet />
        </main>
      </div>

      {/* Mobile drawer */}
      <Dialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-overlay bg-overlay animate-fade-in md:hidden" />
          <Dialog.Content className="fixed left-0 top-0 z-modal h-full w-sidebar max-w-[80vw] flex-col border-r border-border-primary bg-bg-secondary shadow-overlay md:hidden">
            <div className="flex h-topbar items-center justify-between px-4">
              <Wordmark />
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Menü schließen"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                >
                  <X size={14} />
                </button>
              </Dialog.Close>
            </div>
            <NavList
              collapsed={false}
              badge={{ count: pendingCount, pulse }}
              onItemClick={() => setDrawerOpen(false)}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>

      <FeedbackWidget
        repo="alexdhc/dhc-track"
        user={
          authUser
            ? { email: authUser.email, name: authUser.name }
            : undefined
        }
      />
    </div>
  );
}
